// Reunderwriting Module (Tier 3) PR 3 — Lane Pass/Fail + DQR section.
//
// Cloned from components/DealFlow/UnderwritingTab/sections/LanePassFailSection,
// adapted to ReunderwritingCase + the case-level lane-assessment endpoint.
// Same client-side gates as Underwriting spec §5.5:
//   - All checklist items for the lane must be terminal (Approved /
//     Declined / Waived).
//   - Outcome (Pass / Fail) AND DQR must be populated.
//   - If Fail, failureReason must be non-empty.

import { useEffect, useState } from "react";
import { Button, Card } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import {
  useIssueReunderwritingLaneAssessment,
  useReunderwritingAssessments,
  useReunderwritingChecklist,
} from "@/hooks/reunderwriting/useReunderwriting";
import {
  REUNDERWRITING_PERMISSIONS,
  type CriteriaLane,
  type DealQualityRating,
  type LaneOutcome,
  type ReunderwritingChecklistItemDto,
} from "@/types/reunderwriting";

const DQR_OPTIONS: ReadonlyArray<{ value: DealQualityRating; label: string }> = [
  { value: "Excellent", label: "1 — Excellent" },
  { value: "Good", label: "2 — Good" },
  { value: "Satisfactory", label: "3 — Satisfactory" },
  { value: "Fair", label: "4 — Fair" },
  { value: "Poor", label: "5 — Poor" },
];

function isAllTerminal(items: ReunderwritingChecklistItemDto[]): boolean {
  if (items.length === 0) return false;
  return items.every(
    (i) => i.status === "Approved" || i.status === "Declined" || i.status === "Waived",
  );
}

interface Props {
  caseId: string;
  lane: CriteriaLane;
  readOnly?: boolean;
}

export function ReunderwritingLanePassFailSection({ caseId, lane, readOnly = false }: Props) {
  const items = useReunderwritingChecklist(caseId, lane);
  const allAssessments = useReunderwritingAssessments(caseId);
  const existing = allAssessments.data?.find((a) => a.lane === lane);
  const issue = useIssueReunderwritingLaneAssessment(caseId, lane);
  const toast = useToast();
  const auth = useAuth();
  const { dateTime } = useDateFormatter();
  const canIssue = (auth.user?.permissions ?? []).includes(
    REUNDERWRITING_PERMISSIONS.IssueLanePassFail,
  );

  const [outcome, setOutcome] = useState<LaneOutcome | "">("");
  const [dqr, setDqr] = useState<DealQualityRating | "">("");
  const [failureReason, setFailureReason] = useState("");

  useEffect(() => {
    if (existing) {
      setOutcome(existing.outcomePassFail);
      setDqr(existing.dealQualityRating);
      setFailureReason(existing.failureReason ?? "");
    }
  }, [existing]);

  const allTerminal = isAllTerminal(items.data ?? []);
  const issued = !!existing;
  const isValid =
    !!outcome && !!dqr && (outcome === "Pass" || failureReason.trim().length > 0);

  async function onIssue() {
    if (!outcome || !dqr) return;
    try {
      await issue.mutateAsync({
        outcomePassFail: outcome,
        dealQualityRating: dqr,
        failureReason: outcome === "Fail" ? failureReason : null,
      });
      toast.success(`${lane} lane assessment issued.`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  if (issued) {
    return (
      <Card>
        <div className="flex flex-col gap-space-2" data-testid={`lane-assessment-${lane}-issued`}>
          <h4 className="font-display text-h4 m-0">Lane {lane} — Assessment Issued</h4>
          <p className="text-small m-0">
            Outcome: <strong>{existing!.outcomePassFail}</strong> · DQR:{" "}
            <strong>{existing!.dealQualityRating}</strong>
          </p>
          {existing!.failureReason && (
            <p className="text-small text-status-error m-0">
              Failure reason: {existing!.failureReason}
            </p>
          )}
          <p className="text-caption text-gray-mid m-0">
            Issued by {existing!.assessedByUserId} at{" "}
            {dateTime(existing!.assessedAt)}.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex flex-col gap-space-3" data-testid={`lane-assessment-${lane}-form`}>
        <h4 className="font-display text-h4 m-0">Lane {lane} — Pass / Fail + DQR</h4>

        {!allTerminal && (
          <p
            className="text-caption text-gray-mid m-0"
            data-testid={`lane-assessment-${lane}-blocked`}
          >
            All checklist items must be terminal (Approved / Declined / Waived)
            before issuing the lane assessment.
          </p>
        )}

        <fieldset className="flex flex-col gap-space-1" disabled={!allTerminal || readOnly}>
          <legend className="text-label uppercase tracking-label text-gold-shadow">
            Outcome
          </legend>
          {(["Pass", "Fail"] as const).map((v) => (
            <label key={v} className="inline-flex items-center gap-space-2 text-small">
              <input
                type="radio"
                checked={outcome === v}
                onChange={() => setOutcome(v)}
              />
              {v}
            </label>
          ))}
        </fieldset>

        <fieldset className="flex flex-col gap-space-1" disabled={!allTerminal || readOnly}>
          <legend className="text-label uppercase tracking-label text-gold-shadow">
            Deal Quality Rating
          </legend>
          {DQR_OPTIONS.map((opt) => (
            <label key={opt.value} className="inline-flex items-center gap-space-2 text-small">
              <input
                type="radio"
                checked={dqr === opt.value}
                onChange={() => setDqr(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </fieldset>

        {outcome === "Fail" && (
          <label className="flex flex-col gap-space-1">
            <span className="text-label uppercase tracking-label text-gold-shadow">
              Failure reason <span className="text-status-error">*</span>
            </span>
            <textarea
              className="border border-border-light rounded-md p-space-2 text-small min-h-[100px]"
              value={failureReason}
              onChange={(e) => setFailureReason(e.target.value)}
              disabled={!allTerminal || readOnly}
            />
          </label>
        )}

        {!readOnly && canIssue && (
          <div className="flex items-center justify-end">
            <Button onClick={onIssue} disabled={!isValid || !allTerminal || issue.isPending}>
              Issue {lane} assessment
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
